import { TextData, TextMessageData, TextMetricData, TextSummaryData } from 'models/ai-chat/visualization.types';
import React from 'react';

interface TextDisplayProps {
  data: TextData;
}

const TextDisplay: React.FC<TextDisplayProps> = ({ data }) => {
  if (!data) {
    return <div className="text-sm p-2">No data available</div>;
  }

  // Handle metric format
  if (data.format === 'metric') {
    return <MetricDisplay data={data} />;
  }

  // Handle message format
  if (data.format === 'message') {
    return <MessageDisplay data={data} />;
  }

  // Handle summary format
  if (data.format === 'summary') {
    return <SummaryDisplay data={data} />;
  }

  // Fallback for unknown format
  return (
    <div className="text-sm p-2">
      {JSON.stringify(data)}
    </div>
  );
};

// Component for displaying a single metric
const MetricDisplay: React.FC<{ data: TextMetricData }> = ({ data }) => {
  return (
    <div className="p-4 rounded-lg bg-WhiteLilac">
      <div className="text-xs uppercase tracking-wider text-CuttySark">
        {data.label}
      </div>
      <div className="text-4xl font-bold my-2 text-DodgerBlue">
        {data.value}
      </div>
      {data.context && (
        <div className="text-sm text-CuttySark">
          {data.context}
        </div>
      )}
    </div>
  );
};

// Component for displaying a message
const MessageDisplay: React.FC<{ data: TextMessageData }> = ({ data }) => {
  return (
    <div className="p-2">
      <p className="text-LimedSpruce">{data.content}</p>
    </div>
  );
};

// Component for displaying a summary with multiple data points
const SummaryDisplay: React.FC<{ data: TextSummaryData }> = ({ data }) => {
  return (
    <div className="p-4 rounded-lg bg-WhiteLilac">
      <h3 className="text-lg font-semibold mb-4 text-Cello">{data.title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.points.map((point, index) => (
          <div 
            key={index} 
            className="p-3 rounded-lg bg-White border border-Botticelli"
          >
            <div className="text-sm text-CuttySark">
              {point.label}
            </div>
            <div className="text-2xl font-semibold my-1 text-ScienceBlue">
              {point.value}
            </div>
            {point.context && (
              <div className="text-xs text-GullGray">
                {point.context}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="text-sm text-CuttySark mt-4">
        Based on {data.count} data points
      </div>
    </div>
  );
};

export default TextDisplay;